// Thin analytics wrapper for Google Analytics 4 + Meta (Facebook) Pixel.
//
// Both SDKs are loaded in /public/index.html (GA4 G-B04Y3PGWD0, Pixel
// 1945658485717197). This module is the single entry point the React app uses
// to fire events so we can:
//   - dedupe (some funnel steps re-render in StrictMode)
//   - validate event names against the catalog
//   - cleanly no-op when gtag/fbq isn't loaded (dev environments, ad blockers)
//   - mirror conversion events to the Meta Pixel using its standard event names
//
// Event catalog — keep in sync with the GA4 dashboard:
//   quote_started          | first paint of /quote
//   zip_entered            | homeowner provides ZIP (or skips)
//   services_selected      | { count }
//   quote_viewed           | { value (quote $), currency }  ← key funnel conversion
//   ready_to_book_clicked  | { value }                       ← hottest signal
//   lead_submitted         | { value }                       ← funnel conversion
//   signup_started         | first paint of /signup
//   signup_completed       | { method, claimed_lead }        ← B2B acquisition
//   trial_checkout_started | { plan_id }
//   subscription_active    | { value: 1 }                    ← B2B activation
//   invite_page_viewed     | { claimed }
//   invite_claim_clicked   | { claimed }
//   scco_demo_opened       | /for-screen-companies demo modal opened
//   scco_demo_skipped      | { step } — user clicked "Skip · Start trial"

const firedOnce = new Set();

const ALLOWED = new Set([
  "quote_started", "zip_entered", "services_selected", "quantity_set", "mesh_selected",
  "range_viewed", "quote_viewed", "ready_to_book_clicked", "lead_submitted",
  "signup_started", "signup_completed",
  "trial_checkout_started", "subscription_active",
  "invite_page_viewed", "invite_claim_clicked",
  "scco_demo_opened", "scco_demo_skipped",
]);

// Mapping: our internal event names → Meta Pixel "standard" event names.
// Standard events (Lead, CompleteRegistration, Subscribe, etc.) get richer
// reporting and optimization inside Meta Ads Manager than custom events.
// Events not listed here are sent to Meta as `trackCustom` with our own name.
const PIXEL_STANDARD = {
  lead_submitted: "Lead",
  ready_to_book_clicked: "Lead",          // hot-lead is still a Lead in Meta's taxonomy
  signup_completed: "CompleteRegistration",
  subscription_active: "Subscribe",
  trial_checkout_started: "InitiateCheckout",
  quote_started: "ViewContent",
};

/**
 * Fire an analytics event to GA4 and (when appropriate) Meta Pixel.
 * @param {string} name — event name (must be in ALLOWED)
 * @param {object} params — additional event parameters
 * @param {{once?: boolean | string}} opts — when set, the event fires at most once
 *   per page lifetime. Pass a string to use as the dedupe key (otherwise the
 *   event name is used).
 */
export function trackGA(name, params = {}, opts = {}) {
  if (!ALLOWED.has(name)) {
    if (typeof window !== "undefined" && window.__DEV__) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] unknown event", name);
    }
    return;
  }
  if (opts.once) {
    const key = typeof opts.once === "string" ? opts.once : name;
    if (firedOnce.has(key)) return;
    firedOnce.add(key);
  }
  // ---- Google Analytics 4 ----
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch {
    /* swallow — analytics must never break the user's flow */
  }
  // ---- Meta (Facebook) Pixel ----
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      const pixelParams = {};
      if (typeof params.value === "number") {
        pixelParams.value = params.value;
        pixelParams.currency = params.currency || "USD";
      }
      const standard = PIXEL_STANDARD[name];
      if (standard) {
        window.fbq("track", standard, pixelParams);
      } else {
        window.fbq("trackCustom", name, pixelParams);
      }
    }
  } catch {
    /* swallow */
  }
}

/** Reset dedupe — useful for SPA route changes that should re-fire once-events. */
export function resetTrackOnce(key) {
  if (key) firedOnce.delete(key);
  else firedOnce.clear();
}
